import { EditIntegrationWebView } from '../view/EditIntegrationWebView';
import { templateEditIntegration } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { EditIntegrationWebComponent } from '../component/EditIntegrationWebComponent';
import { CreateIntegrationEvent } from '../../addIntegrationPanel/types/CreateIntegrationEvent';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { SensorConnectivityErrors } from '../../sensors/constants';
import {
  INTEGRATIONS_WITHOUT_CONNECTIVITY,
  INTEGRATIONS_WITHOUT_CONNECTIVITY_BUT_WITH_SETUP,
  INTEGRATIONS_WITHOUT_NAME,
  IntegrationType,
} from '../../types/integration-type';
import { O365MailWebComponent } from '../../sensors/o365Mail';
import { O365AuditWebComponent } from '../../sensors/o365Audit';
import { AwsSensorComponent } from '../../sensors/aws';
import { BaseSensorWebComponent } from '../../sensors/base/BaseSensorWebComponent';
import { BehaviorSubject, Observer, Subscription } from 'rxjs';
import { EditSensorEvent } from '../types/events';
import { IntegrationData } from '../../types/integration-data';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { SensorEvent } from '../../sensors/types/sensor-type';
import { SensorEnabledStatus, SensorStatus } from '../../common/types';
import _ from 'lodash';
import { ADOnPremSensorComponent } from '../../sensors/ad-onprem';
import { AzureADSensorComponent } from '../../sensors/azureADAudit';
import { IntuneSensorComponent } from '../../sensors/intune';
import { AzureCWSensorComponent } from '../../sensors/azureCW';
import { GoogleWorkspaceSensorComponent } from '../../sensors/googleWorkspace';
import { GCPSensorComponent } from '../../sensors/gcp/index';
import { AtlassianAuditComponent } from '../../sensors/atlassian/atlassianAudit';
import { AtlassianJiraConfluenceComponent } from '../../sensors/atlassian/atlassianJiraConfluence';

export class EditIntegrationWebController extends BaseWebComponentController {
  public component: EditIntegrationWebComponent;
  public view: EditIntegrationWebView;
  public viewConstructor: typeof EditIntegrationWebView;
  public sensors: BaseSensorWebComponent[] = [];
  public integrationType: IntegrationType;
  public testConnectivitySuccessMessage: string;
  private subscriptions: Subscription[] = [];
  private initiatedSensors = 0;
  private isSensorDataChanged = false;
  private validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private validationObserver: Partial<Observer<any>> = {
    next: () => this.updateValidState(),
  };
  private isSensorAreaSetupValid: boolean;
  private validateNameSubscription: any;
  private mandatorySensors: BaseSensorWebComponent[] = [];
  private emptyFormSensors: BaseSensorWebComponent[] = [];

  protected sensorsByIntegrationMap = new Map<number, Array<any>>();

  constructor(component: EditIntegrationWebComponent) {
    super(component);
    this.sensorsByIntegrationMap.set(IntegrationType.O365, [O365MailWebComponent, O365AuditWebComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.AD_ONPREM, [ADOnPremSensorComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.AWS, [AwsSensorComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.ATLASSIAN_CLOUD, [
      AtlassianAuditComponent,
      AtlassianJiraConfluenceComponent,
    ]);
      this.sensorsByIntegrationMap.set(IntegrationType.AZURE_AD, [AzureADSensorComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.AZURE_CW, [AzureCWSensorComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.INTUNE, [IntuneSensorComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.GOOGLE_WS, [GoogleWorkspaceSensorComponent]);
    this.sensorsByIntegrationMap.set(IntegrationType.GCP, [GCPSensorComponent]);

    this.initialSetup({
      viewConstructor: EditIntegrationWebView,
      viewTemplate: templateEditIntegration,
    });
  }

  private updateValidStateForIntegrationWithSetupWithoutTestConnectivity(): void {
    let areFormsEmpty = true;
    let areNonOptionalSensorsValid = true;
    let message = '';
    for (const sensor of this.emptyFormSensors) {
      if (!sensor.controller.isFormEmpty()) {
        message = this.view.translateAddSensorTooltipMessage(this.integrationType);
        areFormsEmpty = false;
        break;
      }
    }

    for (const sensor of this.mandatorySensors) {
      if (!sensor.valid) {
        areNonOptionalSensorsValid = false;
        break;
      }
    }
    this.isSensorAreaSetupValid = areFormsEmpty && areNonOptionalSensorsValid;
    this.component.dispatchEvent(
      new CustomEvent(EditSensorEvent.isFormValid, {
        detail: {
          isFormValid: this.isSensorAreaSetupValid && this.view.dom.$integrationName.valid,
          addSensorTooltipMessage: message
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private updateValidState(): void {
    if (INTEGRATIONS_WITHOUT_CONNECTIVITY_BUT_WITH_SETUP.includes(this.integrationType)) {
      this.updateValidStateForIntegrationWithSetupWithoutTestConnectivity();
      return;
    }

    const isFormValid = this.isValid();
    this.view.dom.$testConnectivityButton.disabled = !isFormValid;
    this.validSubject.next(isFormValid);
    if (isFormValid && INTEGRATIONS_WITHOUT_CONNECTIVITY.includes(this.integrationType)) {
      this.verifyCompleted();
    }
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$confirmedRequirementsButton,
        event: 'click',
        callback: this.showRequirements,
      },
      {
        element: this.view.dom.$testConnectivityButton,
        event: 'click',
        callback: this.testConnectivity,
      },
      {
        element: this.view.dom.$wrapper,
        event: SensorEvent.enableSensor,
        callback: this.onSensorEnable,
      },
    ];
  }

  private showRequirements(): void {
    this.component.dispatchEvent(
      new CustomEvent(CreateIntegrationEvent.OnShowRequirements, {
        detail: { type: this.integrationType, isEditPanel: true },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * @private
   */
  private async testConnectivity() {
    this.view.dom.$integrationName.touched = true;
    this.view.dom.$integrationName.validate();
    const translations = this.view.getTranslations();
    this.view.dom.$testConnectivityButton.disabled = true;
    this.view.dom.$testConnectivityButton.showSpinner = true;
    try {
      const response = await this.dependencies.api.clients.SensorsIntegrations.testConnectivity(
        await this.getSensorsDetails(true),
        getRequestDefaultOptions()
      );
      if (response['success']) {
        this.isSensorAreaSetupValid = true;
        this.verifyCompleted();
        const toastConfig: GzToast = {
          message: this.testConnectivitySuccessMessage,
          severity: ToastSeverity.INFORMATIVE,
        };
        GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig, undefined, false);
      } else {
        if (response['data'].length === 0) {
          const errorMessage = this.view.translateSensorConnectivityErrorCodes(SensorConnectivityErrors.UNKNOWN_ERROR);
          this.showErrorToast(errorMessage);
          return;
        }

        response['data'].forEach(sensorError => {
          const errorCode = sensorError['errorCode']
            ? sensorError['errorCode']
            : SensorConnectivityErrors.UNKNOWN_ERROR;
          if (sensorError['errorDescription'] && !sensorError['errorDescription'].endsWith('.')) {
            sensorError['errorDescription'] = sensorError['errorDescription'] + '.';
          }
          const errorDescription =
            !sensorError['errorUri'] && sensorError['errorDescription'] ? sensorError['errorDescription'] + ' ' : '';

          const errorMessage =
            translations.sensorNames[sensorError['sensorType']] +
            ': ' +
            errorDescription +
            this.view.translateSensorConnectivityErrorCodes(errorCode);
          this.showErrorToast(errorMessage, sensorError['errorUri']);
        });
      }
    } catch (e) {
      const errorMessage = e?.message || this.translator.getGenericErrorMessage();
      this.showErrorToast(errorMessage);
    } finally {
      this.view.dom.$testConnectivityButton.disabled = false;
      this.view.dom.$testConnectivityButton.showSpinner = false;
    }
  }

  public adaptViewBasedOnType(integrationType: IntegrationType): void {
    if (!this.view.isConnected) {
      return;
    }

    if (INTEGRATIONS_WITHOUT_CONNECTIVITY.includes(integrationType)) {
      this.view.dom.$testConnectivityButton.style.visibility = 'hidden';
      this.isSensorAreaSetupValid = true;
    } else if (INTEGRATIONS_WITHOUT_CONNECTIVITY_BUT_WITH_SETUP.includes(integrationType)) {
      this.view.dom.$testConnectivityButton.style.visibility = 'hidden';
    } else {
      this.view.dom.$testConnectivityButton.style.visibility = 'visible';
      this.isSensorAreaSetupValid = false;
    }

    if (INTEGRATIONS_WITHOUT_NAME.includes(integrationType)) {
      this.view.dom.$integrationName.style.display = 'none';
    } else {
      this.view.dom.$integrationName.style.display = 'inline';
      this.validateNameSubscription = this.view.dom.$integrationName.valid$.subscribe({
        next: valid => {
          if (!valid) {
            //if name is invalid disable save button
            this.component.dispatchEvent(
              new CustomEvent(CreateIntegrationEvent.OnInValidDataCompletion, {
                bubbles: true,
                composed: true,
              }),
            );
            return;
          }
          //check if also the sensor area is valid
          this.verifyCompleted();
        },
      });
    }
    this.view.dom.$integrationName.disabled = false;
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions = [];
    this.view.dom.$sensors = [];
    this.mandatorySensors = [];
    this.emptyFormSensors = [];
    this.initiatedSensors = 0;
    this.isSensorDataChanged = false;
    this.view.dom.$sensorArea.innerHTML = '';
    this.view.dom.$testConnectivityButton.disabled = true;

    this.integrationType = integrationType;
    const sensorsData = this.component.getSensors();
    if (this.sensorsByIntegrationMap.has(integrationType)) {
      this.setSensors(sensorsData, integrationType);
      this.buildSensorArea();
    }
  }

  private setSensors(sensorsData: Array<object> | null, integrationType: IntegrationType): void {
    const sensorsClasses = this.sensorsByIntegrationMap.get(integrationType);
    // @ts-ignore
    for (const cls of sensorsClasses) {
      const sensorComponent = new cls() as BaseSensorWebComponent;
      let sensors: any = [];
      if (sensorsData) {
        if (sensorComponent.isComposed) {
          for (const type of sensorComponent.getComposedSensors()) {
            if (sensorsData[type]) {
              sensors = sensors.concat(sensorsData[type]);
            }
          }
          sensorComponent.setSensors(sensors);
          this.view.dom.$sensors.push(sensorComponent);
          continue;
        }
        sensors = sensorsData[sensorComponent.getSensorType()];
        sensorComponent.setSensorId(sensors[0]._id);
        const sensorsStatus = sensors[0].status;
        sensorComponent.setIsConfigured(sensorsStatus !== SensorStatus.NOT_CONFIGURED);
        this.isSensorDataChanged = true;
      }
      this.view.dom.$sensors.push(sensorComponent);
    }
  }

  private verifyCompleted(): void {
    if (
      INTEGRATIONS_WITHOUT_CONNECTIVITY.includes(this.integrationType) ||
      (this.isSensorAreaSetupValid && this.view.dom.$integrationName.valid)
    ) {
      this.component.dispatchEvent(
        new CustomEvent(CreateIntegrationEvent.OnValidDataCompletion, {
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  public hasChangedData(): boolean {
    return this.isSensorDataChanged;
  }

  public getSensorsByType(sensors): Array<object> {
    const key = 'type';
    return sensors.reduce((hash, sensor) => {
      if (sensor[key] === undefined) {
        return hash;
      }
      return Object.assign(hash, { [sensor[key]]: (hash[sensor[key]] || []).concat(sensor) });
    }, {});
  }

  private dispatchChangeEvent(): void {
    this.component.dispatchEvent(
      new CustomEvent(EditSensorEvent.change, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private buildSensorArea(): void {
    this.view.dom.$sensors.forEach(async sensor => {
      sensor.className = 'mb-5';
      sensor.injectDependencies(this.dependencies);
      sensor
        .start()
        .then(() => {
          this.initiatedSensors++;
          this.subscriptions.push(sensor.valid$.subscribe(this.validationObserver));
          sensor.addEventListener('change', () => {
            this.isSensorAreaSetupValid = false;
            this.isSensorDataChanged = true;
            this.updateValidState();
          });
          if (!sensor.formNeedToBeEmptyForAddedSensor) {
            this.mandatorySensors.push(sensor);
            if (sensor.isEnabled() && sensor.getIsConfigured()) {
              sensor.setEnabled(true);
              if (sensor.valid) {
                //It's not mandatory to have test connectivity for these sensors
                this.view.dom.$testConnectivityButton.disabled = false;
              }
            }
          } else {
            this.emptyFormSensors.push(sensor);
          }
        })
        .catch();

      this.view.dom.$sensorArea.appendChild(sensor);
    });
  }

  private isInitialized(): boolean {
    return this.view.dom.$sensors.length === this.initiatedSensors;
  }

  private isValid(): boolean {
    if (!this.isInitialized()) {
      return false;
    }

    for (const sensor of this.view.dom.$sensors) {
      // consider integration as valid for test connectivity if at least one sensor is valid
      if (sensor.isEnabled() && sensor.valid) {
        return true;
      }
    }
    return false;
  }

  async getValue(): Promise<IntegrationData> {
    const integrationData = {
      name: this.view.dom.$integrationName.value as string,
      type: this.integrationType,
      sensors: await this.getSensorsDetails(),
      integrationSpecifics: this.getIntegrationSpecifics(),
    };

    switch (this.integrationType) {
      case IntegrationType.AD_ONPREM:
        integrationData.name = integrationData.integrationSpecifics['domainName'] as string;
        delete integrationData.integrationSpecifics['domainName'];
        break;
      default:
        break;
    }

    if (this.component.getIntegrationId()) {
      integrationData['_id'] = this.component.getIntegrationId();
    }
    return integrationData;
  }

  private getIntegrationSpecifics(): FormGroupValue {
    let integrationSpecifics = {} as FormGroupValue;
    this.view.dom.$sensors.forEach(sensor => {
      integrationSpecifics = sensor.getSpecifics();
    });
    return integrationSpecifics;
  }

  private async getSensorsDetails(skipIfDisabled = false): Promise<FormGroupValue[]> {
    let sensorsDetails = [] as FormGroupValue[];

    switch (this.integrationType) {
      case IntegrationType.AD_ONPREM:
      case IntegrationType.NSVA:
        sensorsDetails = [];
        break;
      case IntegrationType.O365:
      case IntegrationType.ATLASSIAN_CLOUD:
      case IntegrationType.AWS:
      case IntegrationType.AZURE_AD:
      case IntegrationType.INTUNE:
      case IntegrationType.AZURE_CW:
      case IntegrationType.GOOGLE_WS:
      case IntegrationType.GCP:
        sensorsDetails = await this.getBaseSensorsDetails(skipIfDisabled);
        break;
      default:
        break;
    }

    return sensorsDetails;
  }

  private async getBaseSensorsDetails(skipIfDisabled: boolean): Promise<FormGroupValue[]> {
    const sensorsDetails = [] as FormGroupValue[];
    for (const sensor of this.view.dom.$sensors) {
      if (!sensor.isEnabled() && skipIfDisabled) {
        continue;
      }

      if (sensor.hasDetails) {
        sensorsDetails.push(...sensor.getDetails());
        continue;
      }

      const sensorValue = sensor.getValue();
      sensorValue['type'] = sensor.getSensorType();

      if (sensor.isEnabled()) {
        sensorValue['enabledStatus'] = SensorEnabledStatus.ENABLED;
      } else {
        sensorValue['enabledStatus'] = sensor.getIsConfigured()
          ? SensorEnabledStatus.DISABLED
          : SensorEnabledStatus.NOT_CONFIGURED;
      }

      if (sensor.getSensorId()) {
        sensorValue['_id'] = sensor.getSensorId();
      }

      //@ts-ignore
      if (
        sensorValue.hasOwnProperty('private_key') &&
        //@ts-ignore
        sensorValue['private_key'].startsWith('-----BEGIN PRIVATE KEY-----\\n')
      ) {
        sensorValue['private_key'] = JSON.parse('"' + sensorValue['private_key'] + '"');
      }
      for (const key in sensorValue) {
        if (sensorValue[key] instanceof FileList) {
          const fileValue = sensorValue[key] as FileList;
          if (fileValue.item(0)) {
            const file = fileValue.item(0) as File;
            sensorValue[key] = JSON.parse(await file.text());
          } else {
            delete sensorValue[key];
          }
        }
      }
      if (!_.isEmpty(sensorValue)) {
        sensorsDetails.push(sensorValue);
      }
    }

    return sensorsDetails;
  }

  /**
   * @param errorMessage
   * @param viewMoreURI
   * @private
   */
  private showErrorToast(errorMessage: string, viewMoreURI: string | null = null) {
    const toastConfig: GzToast = {
      message: errorMessage,
      severity: ToastSeverity.ERROR,
      keepOpen: true,
    };

    if (viewMoreURI) {
      toastConfig.hasAction = true;
      toastConfig.actionButtonText = this.dependencies.translateFn('View more');
      toastConfig.showInlineButton = true;
      toastConfig.actionCallback = () => {
        return window.open(viewMoreURI, '_blank');
      };
    }

    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig, undefined, false);
  }

  private onSensorEnable(ev: WebComponentEvent): void {
    this.updateValidState();
  }
}
